import { useEffect, useState } from "react";
import { api, API_BASE, MARKETPLACES } from "../lib/api";
import { MarketplaceTag, HubStatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { Loader2, Printer, FileText, Layers } from "lucide-react";

export default function Printing() {
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("label_guide");
  const [filters, setFilters] = useState({ company_id: "", marketplace: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && q.append(k, v));
      const { data } = await api.get(`/orders?${q.toString()}`);
      // Only show orders ready for printing
      setOrders(data.filter((o) => ["nf_sent", "label_ready", "in_separation", "separated", "packed"].includes(o.hub_status)));
    } catch (e) {
      toast.error("Erro ao listar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === orders.length) setSelected(new Set());
    else setSelected(new Set(orders.map((o) => o.id)));
  };

  const print = async () => {
    if (selected.size === 0) {
      toast.warning("Selecione ao menos um pedido");
      return;
    }
    setBusy(true);
    try {
      const resp = await api.post("/print/combined", { order_ids: Array.from(selected), mode }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      toast.success(`Impressão consolidada gerada (${selected.size} pedidos)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na impressão");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="printing-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Central</div>
        <h1 className="text-3xl font-black tracking-tighter">Impressão Inteligente</h1>
        <p className="text-sm text-slate-600 mt-1">Etiqueta + Guia consolidadas em PDF único — pronto para a expedição.</p>
      </div>

      <div className="border border-slate-200 bg-white p-3 grid grid-cols-1 md:grid-cols-5 gap-2">
        <select data-testid="print-filter-company" value={filters.company_id} onChange={(e) => setFilters({ ...filters, company_id: e.target.value })} className="border border-slate-300 rounded-sm px-2 py-2 text-sm">
          <option value="">Todas empresas</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.nome_fantasia}</option>)}
        </select>
        <select data-testid="print-filter-marketplace" value={filters.marketplace} onChange={(e) => setFilters({ ...filters, marketplace: e.target.value })} className="border border-slate-300 rounded-sm px-2 py-2 text-sm">
          <option value="">Todos marketplaces</option>
          {MARKETPLACES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <select data-testid="print-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm">
          <option value="label_guide">Etiqueta + Guia (intercalado)</option>
          <option value="label">Somente Etiquetas</option>
          <option value="guide">Somente Guias</option>
        </select>
        <button data-testid="print-toggle-all" onClick={toggleAll} className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900">
          {selected.size === orders.length && orders.length > 0 ? "Limpar seleção" : "Selecionar todos"}
        </button>
        <button data-testid="print-action" onClick={print} disabled={busy || selected.size === 0} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
          Imprimir {selected.size > 0 ? `(${selected.size})` : ""}
        </button>
      </div>

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600">
            <tr>
              <th className="px-3 py-2.5 w-8"></th>
              <th className="text-left px-3 py-2.5">Pedido</th>
              <th className="text-left px-3 py-2.5">Marketplace</th>
              <th className="text-left px-3 py-2.5">Cliente</th>
              <th className="text-left px-3 py-2.5">SKUs</th>
              <th className="text-left px-3 py-2.5">Status Vyntric</th>
              <th className="text-right px-3 py-2.5">Individual</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="7" className="py-12 text-center"><Loader2 className="inline w-5 h-5 animate-spin text-slate-400" /></td></tr>)}
            {!loading && orders.length === 0 && (
              <tr><td colSpan="7" className="py-12 text-center text-sm text-slate-500">Nenhum pedido pronto para impressão. Aprove e envie ao ERP primeiro.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="t-row border-b border-slate-100" data-testid={`print-row-${o.order_number}`}>
                <td className="px-3 py-2.5">
                  <input
                    data-testid={`print-check-${o.order_number}`}
                    type="checkbox"
                    checked={selected.has(o.id)}
                    onChange={() => toggle(o.id)}
                  />
                </td>
                <td className="px-3 py-2.5 font-mono text-xs font-bold">{o.order_number}</td>
                <td className="px-3 py-2.5"><MarketplaceTag id={o.marketplace} /></td>
                <td className="px-3 py-2.5 text-xs">{o.customer_name}</td>
                <td className="px-3 py-2.5 text-[10px] font-mono text-slate-600">
                  {o.items.map((it) => `${it.sku}×${it.quantity}`).join("  ·  ")}
                </td>
                <td className="px-3 py-2.5"><HubStatusBadge status={o.hub_status} /></td>
                <td className="px-3 py-2.5 text-right">
                  <a data-testid={`print-guide-${o.order_number}`} href={`${API_BASE}/orders/${o.id}/picking-guide.pdf`} target="_blank" rel="noreferrer" className="inline-block p-1.5 hover:bg-slate-200 text-slate-600" title="Guia"><FileText className="w-4 h-4" /></a>
                  <a data-testid={`print-label-${o.order_number}`} href={`${API_BASE}/orders/${o.id}/label.pdf`} target="_blank" rel="noreferrer" className="inline-block p-1.5 hover:bg-slate-200 text-slate-600" title="Etiqueta"><Printer className="w-4 h-4" /></a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
